import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'auth/hooks/useAuth';
import {
  CREATION_USERS_PATH,
  DETAIL_USERS_PATH,
  EDIT_USERS_PATH,
  FORBIDDEN_PATH,
  IMPERSONATION_HOME_PATH,
  LIST_USERS_PATH,
  LOGIN_PATH,
} from 'user/routes';
import { HOME_PATH } from 'navigation/routes';
import { CONSULTAR_REPORTE_PATH } from 'core/reporte/routes';
import { CARD_ASSOCIATE_TO_COSTCENTER, LIST_CARDS_PATH } from 'core/tarjeta/routes';
import {
  COST_CENTRE_CREATE_PATH,
  COST_CENTRE_EDIT_PATH,
  COST_CENTRE_LIST_PATH,
  COST_CENTRE_MOVE_PATH,
} from 'core/centrocosto/routes';
import {
  ACCOUNT_DISTRIBUTION_PATH,
  ACCOUNT_REFUND_PATH,
  ACCOUNT_TRANSFER_HISTORY_PATH,
} from 'core/cuenta/routes';
import {
  TRANSACTIONS_SUMMARY_PATH,
  TRANSACTIONS_HISTORY_PATH,
} from 'core/movimientos/routes';
import { REPORT_MENU_PERMISSIONS } from 'core/reporte/util/reportPermissions';

interface RouteGuard {
  pathname: string;
  functions: string[];
}

const ROUTES: RouteGuard[] = [
  {
    pathname: HOME_PATH,
    functions: ['PERMITIR'],
  },
  {
    pathname: IMPERSONATION_HOME_PATH,
    functions: ['IMPERSONACION_MENU'],
  },
  {
    pathname: LIST_USERS_PATH,
    functions: ['ABM_USUARIO'],
  },
  {
    pathname: CREATION_USERS_PATH,
    functions: ['ABM_USUARIO'],
  },
  {
    pathname: DETAIL_USERS_PATH,
    functions: ['ABM_USUARIO'],
  },
  {
    pathname: EDIT_USERS_PATH,
    functions: ['ABM_USUARIO'],
  },
  {
    pathname: LIST_CARDS_PATH,
    functions: ['ABM_TARJETA'],
  },
  {
    pathname: CARD_ASSOCIATE_TO_COSTCENTER,
    functions: ['ABM_TARJETA'],
  },
  {
    pathname: COST_CENTRE_LIST_PATH,
    functions: ['ABM_CENTRO_COSTO', 'PERMITIR'],
  },
  {
    pathname: COST_CENTRE_CREATE_PATH,
    functions: ['ABM_CENTRO_COSTO'],
  },
  {
    pathname: COST_CENTRE_EDIT_PATH,
    functions: ['ABM_CENTRO_COSTO'],
  },
  {
    pathname: COST_CENTRE_MOVE_PATH,
    functions: ['ABM_CENTRO_COSTO'],
  },
  {
    pathname: ACCOUNT_REFUND_PATH,
    functions: ['DEVOLUCION_TOTAL', 'DEVOLUCION_PARCIAL'],
  },
  {
    pathname: ACCOUNT_TRANSFER_HISTORY_PATH,
    functions: ['CONSULTA_TRANSFERENCIA'],
  },
  {
    pathname: ACCOUNT_DISTRIBUTION_PATH,
    functions: ['DISTRIBUCION'],
  },
  {
    pathname: CONSULTAR_REPORTE_PATH,
    functions: [...REPORT_MENU_PERMISSIONS],
  },
  {
    pathname: TRANSACTIONS_HISTORY_PATH,
    functions: ['CONSULTA_CUENTA'],
  },
  {
    pathname: TRANSACTIONS_SUMMARY_PATH,
    functions: ['CONSULTA_CUENTA'],
  },
];

export function PrivateRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const route = ROUTES.find((r) =>
    location.pathname.startsWith(r.pathname.replace(/:\w+/, '')),
  );

  if (!user) {
    return <Navigate to={LOGIN_PATH} />;
  }

  const funcionalidades = user.funcionalidades || [];

  // Caso especial: usuario dummy que se carga en el estado mientras se cargan los datos del usuario real
  const cargandoUsuario =
    funcionalidades.length === 1 && funcionalidades[0] === 'CARGANDO';

  // Verificación de permisos de la ruta
  const tienePermisoRuta = route?.functions?.some((f) => funcionalidades.includes(f));

  // Si la ruta es privada pero no está en la lista, o si el usuario no tiene los permisos, se deniega el acceso.
  const accesoDenegado = !cargandoUsuario && (!route || !tienePermisoRuta);
  if (accesoDenegado) {
    return <Navigate to={FORBIDDEN_PATH} />;
  }

  return <Outlet />;
}
